import { ReactElement, useContext, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { CopyToClipboard } from '@10xLabs/react-web/components/CopyToClipboard'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Drawer from 'components/v2/Drawer'
import Heading from 'components/v2/Heading'
import EmptyState from 'components/v2/EmptyState'
import { device } from 'utils/style'
import { NetworkStatus, useQuery } from '@apollo/client'
import { Button } from 'components/v2/Button'
import { SelectedTripsContext } from 'features/Trips/views/Index'
import { TabView, TabsSelector } from 'components/v2/TabSlider'
import { TripTimeline } from 'features/Trips/components/TripDrawer/Timeline'
import { TripNotes } from 'features/Trips/components/TripDrawer/Notes'
import { TripDetails } from 'features/Trips/components/TripDrawer/Details'
import { GET_TRIP_V2 } from 'features/Trips/queries'
import { TripV2 } from 'gql/generated/graphql'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media ${device.tablet} {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }
`

const TripIdLabel = styled.div`
  align-items: center;
  color: #b0b0b0;
  display: flex;
  font-size: 18px;
`

export const ViewHeader = ({ title, tripId }: { title: string; tripId: string }): ReactElement => {
  const shortTripId = tripId?.substring(0, 8).toUpperCase() ?? ''

  return (
    <HeaderWrapper>
      <Heading.H5>{title}</Heading.H5>
      <TripIdLabel>
        <FormattedMessage
          id="trip.drawer.timeline.tripId"
          defaultMessage="Trip ID: {tripId}"
          values={{ tripId: shortTripId }}
        />
        <CopyToClipboard str={tripId} />
      </TripIdLabel>
    </HeaderWrapper>
  )
}

const tabsMessages = defineMessages({
  details: { id: 'trip.drawer.details.title', defaultMessage: 'Details' },
  timeline: { id: 'trip.drawer.timeline.title', defaultMessage: 'Timeline' },
  notes: { id: 'trip.drawer.notes.title', defaultMessage: 'Notes' }
})

export const TripDrawer = (): ReactElement => {
  const intl = useIntl()
  const [activeIndex, setActiveIndex] = useState(0)
  const { selectedTripId, setSelectedTripId } = useContext(SelectedTripsContext)
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_TRIP_V2, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !selectedTripId,
    variables: {
      input: {
        id: selectedTripId
      }
    }
  })

  let content = <></>
  if (loading || networkStatus === NetworkStatus.refetch) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="trips.drawer.error.description"
            defaultMessage="There was an error while loading the trip"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            kind="primary"
            onClick={(): void => {
              refetch()
            }}
          >
            <FormattedMessage id="trips.drawer.error.action" defaultMessage="Reload trip" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    const tabs = [
      intl.formatMessage(tabsMessages.details),
      intl.formatMessage(tabsMessages.timeline),
      intl.formatMessage(tabsMessages.notes)
    ]
    const trip = data?.tripV2 as TripV2
    const tripTimeline = trip?.timeline ?? []
    const tripNotes = trip?.notes ?? []

    content = (
      <Wrapper>
        <TabsSelector activeIndex={activeIndex} items={tabs} onChange={setActiveIndex} />
        <ViewHeader title={tabs[activeIndex]} tripId={selectedTripId ?? ''} />
        <TabView activeIndex={activeIndex}>
          {!!trip && (
            <>
              <TripDetails trip={trip as TripV2} />
              <TripTimeline timeline={[...tripTimeline].reverse()} trip={trip as TripV2} />
              <TripNotes tripId={trip.id} notes={tripNotes} driver={trip.driver} />
            </>
          )}
        </TabView>
      </Wrapper>
    )
  }

  return (
    <Drawer.Container isOpen={!!selectedTripId} onClose={(): void => setSelectedTripId(undefined)}>
      {content}
    </Drawer.Container>
  )
}
